import { inject, Injectable } from '@angular/core';
import { ApiService } from '../../../../core/services/ApiService';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { IResponse } from '../../../../core/interfaces/IResponse';
import { ITerminal } from '../interfaces/ITerminal';
import { TerminalsStore } from '../stores/TerminalsStore';
import { ITerminalShowCashPrice } from '../interfaces/ITerminalShowCashPrice';
import { INoDataResponse } from '../../../../core/interfaces/INoDataResponse';
import { ITerminalSessionTransaction } from '../interfaces/ITerminalSessionTransaction';
import { IdentityStore } from '../../../auth/core/stores/IdentityStore';
import { AuthService } from '../../../auth/core/services/AuthService';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class TerminalsService {

  private readonly _basePath = '/terminals';
  private readonly _apiService = inject(ApiService);
  private readonly _terminalsStore = inject(TerminalsStore);
  private readonly _identityStore = inject(IdentityStore);
  private readonly _authService = inject(AuthService);

  public getTerminals(): Observable<IResponse<ITerminal[]>> {
    return this._apiService.get(`${this._basePath}`).pipe(
      tap(res => this._terminalsStore.terminals.set(res.data))
    );
  }

  public getTerminalById(id: number): Observable<IResponse<ITerminal>> {
    return this._apiService.get(`${this._basePath}/${id}`).pipe(
      this.handleTerminalError(),
      tap(res => this._terminalsStore.selectedTerminal.set(res.data))
    );
  }

  public getTerminalBySN(sn: number): Observable<IResponse<ITerminal>> {
    return this._apiService.get(`${this._basePath}/${sn}/by-number`).pipe(
      this.handleTerminalError(),
      tap(res => this._terminalsStore.selectedTerminal.set(res.data))
    );
  }

  public pairSumUpReader(pairingCode: string): Observable<IResponse<{ readerId: string }>> {
    return this._apiService.post(`${this._basePath}/sumup-reader`, { pairingCode });
  }

  public openSession(terminalId: number, safeNumber: number): Observable<INoDataResponse> {
    return this._apiService.post(
      `${this._basePath}/open-terminal-session`,
      { terminalId, safeNumber: safeNumber.toString() }
    );
  }

  public getTerminalSessionTransactions(): Observable<IResponse<ITerminalSessionTransaction[]>> {
    return this._apiService.get(`${this._basePath}/session-transactions`);
  }

  public closeTerminalSession(terminalSessionId: number): Observable<INoDataResponse> {
    return this._apiService.post(`${this._basePath}/close-terminal-session`, { terminalSessionId });
  }

  public pauseTerminalSession(terminalSessionId: number): Observable<INoDataResponse> {
    return this._apiService.post(`${this._basePath}/pause-terminal-session`, { terminalSessionId });
  }

  public continueTerminalSession(terminalSessionId: number, safeNumber: number): Observable<INoDataResponse> {
    return this._apiService.post(
      `${this._basePath}/continue-terminal-session`,
      { terminalSessionId, safeNumber: safeNumber?.toString() ?? '0' }
    );
  }

  public getTerminalShowCashPrices(showId: number): Observable<IResponse<ITerminalShowCashPrice[]>> {
    return this._apiService.get(`${this._basePath}/cash-prices?showId=${showId}`)
      .pipe(
        tap(res => this._terminalsStore.terminalShowCashPrices.set(res.data))
      );
  }

  private handleTerminalError(): ReturnType<typeof catchError> {
    return catchError(error => {
      if (this._identityStore.identity()) {
        this._authService.logout();
      }

      return throwError(() => error);
    });
  }
}
